import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createLocation,
  getLocationDetails,
  updateLocation,
} from '../../services/locationService';
import type { LocationDetails } from '../../types/location';
import {
  isConcurrencyConflict,
  organizationErrorMessage,
} from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<RootStackParamList, 'LocationCreate'>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'LocationEdit'>;
type Props = CreateProps | EditProps;

type FormState = {
  locationCode: string;
  locationName: string;
  locationType: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateName: string;
  countryCode: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  geofenceRadiusMeters: string;
  timeZone: string;
  contactPerson: string;
  contactNumber: string;
  isHeadOffice: boolean;
  isAttendanceEnabled: boolean;
  isActive: boolean;
};

const EMPTY: FormState = {
  locationCode: '',
  locationName: '',
  locationType: 'OFFICE',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateName: '',
  countryCode: '',
  postalCode: '',
  latitude: '',
  longitude: '',
  geofenceRadiusMeters: '',
  timeZone: '',
  contactPerson: '',
  contactNumber: '',
  isHeadOffice: false,
  isAttendanceEnabled: true,
  isActive: true,
};

function fromDetails(item: LocationDetails): FormState {
  return {
    locationCode: item.locationCode,
    locationName: item.locationName,
    locationType: item.locationType,
    addressLine1: item.addressLine1 ?? '',
    addressLine2: item.addressLine2 ?? '',
    city: item.city,
    stateName: item.stateName,
    countryCode: item.countryCode,
    postalCode: item.postalCode ?? '',
    latitude: item.latitude != null ? String(item.latitude) : '',
    longitude: item.longitude != null ? String(item.longitude) : '',
    geofenceRadiusMeters:
      item.geofenceRadiusMeters != null
        ? String(item.geofenceRadiusMeters)
        : '',
    timeZone: item.timeZone,
    contactPerson: item.contactPerson ?? '',
    contactNumber: item.contactNumber ?? '',
    isHeadOffice: item.isHeadOffice,
    isAttendanceEnabled: item.isAttendanceEnabled,
    isActive: item.isActive !== false,
  };
}

export function validateLocationForm(
  form: FormState,
  isEdit: boolean,
): string | null {
  if (!isEdit && !form.locationCode.trim()) return 'Location code is required.';
  if (!form.locationName.trim()) return 'Location name is required.';
  if (!form.locationType.trim()) return 'Location type is required.';
  if (!form.city.trim()) return 'City is required.';
  if (!form.stateName.trim()) return 'State is required.';
  if (form.countryCode.trim().length !== 2) {
    return 'Country code must be exactly two letters.';
  }
  if (!form.timeZone.trim()) return 'Timezone is required.';
  if (form.latitude.trim()) {
    const value = Number(form.latitude);
    if (!Number.isFinite(value) || value < -90 || value > 90) {
      return 'Latitude must be between -90 and 90.';
    }
  }
  if (form.longitude.trim()) {
    const value = Number(form.longitude);
    if (!Number.isFinite(value) || value < -180 || value > 180) {
      return 'Longitude must be between -180 and 180.';
    }
  }
  return null;
}

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'LocationEdit';
}

export function LocationFormScreen(props: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
      switchLabel: { ...typography.body, color: palette.textPrimary },
    }),
    [palette],
  );

  const isEdit = isEditRoute(props);
  const locationId = isEdit ? props.route.params.locationId : null;
  const navigation = props.navigation;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [details, setDetails] = useState<LocationDetails | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const data = await getLocationDetails(locationId);
      setDetails(data);
      setForm(fromDetails(data));
      dirty.current = false;
      setError(null);
    } catch (caught) {
      setError(
        organizationErrorMessage(
          caught,
          'The requested record could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (isEdit) load().catch(() => undefined);
  }, [isEdit, load]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => ({ ...current, [key]: value }));
  }

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  async function submit() {
    if (savingLock.current) return;
    const validation = validateLocationForm(form, isEdit);
    if (validation) {
      setFieldError(validation);
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);

    const latitude = form.latitude.trim()
      ? Number(form.latitude)
      : null;
    const longitude = form.longitude.trim()
      ? Number(form.longitude)
      : null;
    const geofenceRadiusMeters = form.geofenceRadiusMeters.trim()
      ? Number(form.geofenceRadiusMeters)
      : null;

    try {
      if (isEdit && locationId && details) {
        await runOperation('update', () => updateLocation(locationId, {
          locationName: form.locationName,
          locationType: form.locationType,
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2,
          city: form.city,
          stateName: form.stateName,
          countryCode: form.countryCode,
          postalCode: form.postalCode,
          latitude,
          longitude,
          geofenceRadiusMeters,
          timeZone: form.timeZone,
          contactPerson: form.contactPerson,
          contactNumber: form.contactNumber,
          isHeadOffice: form.isHeadOffice,
          isAttendanceEnabled: form.isAttendanceEnabled,
          isActive: form.isActive,
          rowVersion: details.rowVersion,
        }));
      } else {
        await runOperation('add', () => createLocation({
          locationCode: form.locationCode,
          locationName: form.locationName,
          locationType: form.locationType,
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2,
          city: form.city,
          stateName: form.stateName,
          countryCode: form.countryCode,
          postalCode: form.postalCode,
          latitude,
          longitude,
          geofenceRadiusMeters,
          timeZone: form.timeZone,
          contactPerson: form.contactPerson,
          contactNumber: form.contactNumber,
          isHeadOffice: form.isHeadOffice,
          isAttendanceEnabled: form.isAttendanceEnabled,
        }));
      }
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught) && isEdit) {
        const message = organizationErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          organizationErrorMessage(
            caught,
            isEdit ? 'Unable to update location.' : 'Unable to create location.',
          ),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  const permission = isEdit
    ? PERMISSIONS.LOCATION_UPDATE
    : PERMISSIONS.LOCATION_CREATE;

  return (
    <PermissionGuard permission={permission} fallback={<UnauthorizedScreen />}>
      {loading ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title={isEdit ? 'Edit location' : 'Create location'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading location" />
        </AppScreen>
      ) : error && isEdit && !details ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit location"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title={isEdit ? 'Edit location' : 'Create location'}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {isEdit ? (
              <>
                <Text style={themed.readOnlyLabel}>Location code</Text>
                <Text style={themed.readOnlyValue}>{form.locationCode}</Text>
              </>
            ) : (
              <AppInput
                label="Location code *"
                value={form.locationCode}
                onChangeText={value => setField('locationCode', value)}
                autoCapitalize="characters"
              />
            )}
            <AppInput
              label="Location name *"
              value={form.locationName}
              onChangeText={value => setField('locationName', value)}
            />
            <AppInput
              label="Location type *"
              value={form.locationType}
              onChangeText={value => setField('locationType', value)}
              autoCapitalize="characters"
            />
            <AppInput
              label="Address line 1"
              value={form.addressLine1}
              onChangeText={value => setField('addressLine1', value)}
            />
            <AppInput
              label="Address line 2"
              value={form.addressLine2}
              onChangeText={value => setField('addressLine2', value)}
            />
            <AppInput
              label="City *"
              value={form.city}
              onChangeText={value => setField('city', value)}
            />
            <AppInput
              label="State *"
              value={form.stateName}
              onChangeText={value => setField('stateName', value)}
            />
            <AppInput
              label="Country code * (2 letters)"
              value={form.countryCode}
              onChangeText={value => setField('countryCode', value)}
              autoCapitalize="characters"
            />
            <AppInput
              label="Postal code"
              value={form.postalCode}
              onChangeText={value => setField('postalCode', value)}
            />
            <AppInput
              label="Timezone *"
              value={form.timeZone}
              onChangeText={value => setField('timeZone', value)}
              autoCapitalize="none"
            />
            <AppInput
              label="Latitude"
              value={form.latitude}
              onChangeText={value => setField('latitude', value)}
              keyboardType="numeric"
            />
            <AppInput
              label="Longitude"
              value={form.longitude}
              onChangeText={value => setField('longitude', value)}
              keyboardType="numeric"
            />
            <AppInput
              label="Geofence radius (meters)"
              value={form.geofenceRadiusMeters}
              onChangeText={value => setField('geofenceRadiusMeters', value)}
              keyboardType="number-pad"
            />
            <AppInput
              label="Contact person"
              value={form.contactPerson}
              onChangeText={value => setField('contactPerson', value)}
            />
            <AppInput
              label="Contact number"
              value={form.contactNumber}
              onChangeText={value => setField('contactNumber', value)}
              keyboardType="phone-pad"
            />
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Head office</Text>
              <Switch
                value={form.isHeadOffice}
                onValueChange={value => setField('isHeadOffice', value)}
              />
            </View>
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Attendance enabled</Text>
              <Switch
                value={form.isAttendanceEnabled}
                onValueChange={value => setField('isAttendanceEnabled', value)}
              />
            </View>
            {isEdit ? (
              <View style={styles.switchRow}>
                <Text style={themed.switchLabel}>Active</Text>
                <Switch
                  value={form.isActive}
                  onValueChange={value => setField('isActive', value)}
                />
              </View>
            ) : null}
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton title="Save" onPress={submit} loading={saving} />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={confirmLeave}
            />
          </AppCard>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
